import { useState } from 'react';
import {
  MEASUREMENT_UNIT_LABELS,
  QUANTITY_INPUT_MODE_LABELS,
  type QuantityDto,
} from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { Spinner } from '../../components/ui/Spinner';
import { QuantityDialog } from './QuantityDialog';
import { useQuantities, useValidateQuantity } from './hooks';

function truncateHash(hash: string): string {
  return hash.length > 10 ? `${hash.slice(0, 10)}…` : hash;
}

/** Quantities of one element: list, add dialog and four-eyes validation. */
export function ElementQuantities({ elementId }: { elementId: string }): JSX.Element {
  const quantitiesQuery = useQuantities(elementId);
  const validate = useValidateQuantity(elementId);
  const [adding, setAdding] = useState(false);
  const [toValidate, setToValidate] = useState<QuantityDto | null>(null);

  const confirmValidate = () => {
    if (!toValidate) return;
    validate.mutate(toValidate.id, { onSuccess: () => setToValidate(null) });
  };

  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      {/* Four-eyes 403 (« Un autre utilisateur doit valider ») and other API errors. */}
      {validate.isError ? (
        <Alert variant="error" details={toDisplayError(validate.error).details}>
          {toDisplayError(validate.error).message}
        </Alert>
      ) : null}

      {quantitiesQuery.isLoading ? (
        <Spinner label="Chargement des quantités…" />
      ) : quantitiesQuery.isError ? (
        <Alert variant="error">{toDisplayError(quantitiesQuery.error).message}</Alert>
      ) : quantitiesQuery.data && quantitiesQuery.data.length > 0 ? (
        <ul className="space-y-2">
          {quantitiesQuery.data.map((q) => (
            <li
              key={q.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">
                  {q.calculatedQuantity} {MEASUREMENT_UNIT_LABELS[q.measurementUnit]}
                </span>
                <Badge tone="info">{QUANTITY_INPUT_MODE_LABELS[q.inputMode]}</Badge>
                {q.validatedById ? (
                  <Badge tone="success">Validée</Badge>
                ) : (
                  <Badge tone="warning">À valider</Badge>
                )}
                <span className="font-mono text-xs text-slate-400" title={q.snapshotHash}>
                  {truncateHash(q.snapshotHash)}
                </span>
              </div>
              {!q.validatedById ? (
                <Button type="button" size="sm" variant="outline" onClick={() => setToValidate(q)}>
                  Valider
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">Aucune quantité pour cet élément.</p>
      )}

      <Button type="button" size="sm" variant="secondary" onClick={() => setAdding(true)}>
        Ajouter une quantité
      </Button>

      <QuantityDialog elementId={elementId} open={adding} onClose={() => setAdding(false)} />

      <ConfirmDialog
        open={toValidate !== null}
        onClose={() => setToValidate(null)}
        onConfirm={confirmValidate}
        title="Valider la quantité"
        description={
          toValidate
            ? `Valider la quantité de ${toValidate.calculatedQuantity} ${
                MEASUREMENT_UNIT_LABELS[toValidate.measurementUnit]
              } ? Cette validation engage le principe des quatre yeux.`
            : undefined
        }
        confirmLabel="Confirmer"
        loading={validate.isPending}
      />
    </div>
  );
}
